/**
 * Photorealistic mobile load ladder — budget levels + escalate rules.
 * Docs: docs/PHOTOREALISTIC_MOBILE_FALLBACK.md
 */

const MB = 1024 * 1024;

/** Wall-clock seconds per Photorealistic snapshot attempt before escalate. */
export const TILE_ATTEMPT_TIMEOUT_SEC = 20;

/** Chromium heap ratio that forces escalate (when performance.memory exists). */
export const HEAP_PRESSURE_RATIO = 0.85;

/** Tile content load-error count that forces escalate within one attempt. */
export const TILE_ERROR_ESCALATE_COUNT = 8;

/**
 * @typedef {'full' | 'half' | 'quarter'} TileSnapshotId
 * @typedef {{
 *   id: TileSnapshotId,
 *   scale: number,
 *   maxBytes: number,
 *   minBytes: number,
 *   maxSize: number,
 *   minSize: number,
 *   errorTarget: number,
 *   downloadJobs: number,
 *   parseJobs: number,
 *   dprMax: number,
 * }} TileSnapshotBudget
 */

/** Desktop step-1 (“full”) caps — still below library defaults (~400 MB). */
const DESKTOP_FULL_BYTES = 256 * MB;
/** Mobile step-1 (“full”) — aggressive enough to avoid Safari tab kills. */
const MOBILE_FULL_BYTES = 96 * MB;

/**
 * True when we should use the mobile-class full budget.
 * Coarse pointer, small viewport, or low deviceMemory.
 */
export function isMobileTileDevice() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia?.('(pointer: coarse)').matches) return true;
    if (window.matchMedia?.('(max-width: 1100px) and (max-height: 520px)').matches) {
      return true;
    }
  } catch {
    /* ignore */
  }
  const mem = Number(navigator.deviceMemory);
  if (Number.isFinite(mem) && mem > 0 && mem <= 4) return true;
  return false;
}

/**
 * Build full → half → quarter budgets from a device-class full byte cap.
 * @param {boolean} [mobile]
 * @returns {TileSnapshotBudget[]}
 */
export function buildTileSnapshotBudgets(mobile = isMobileTileDevice()) {
  const fullBytes = mobile ? MOBILE_FULL_BYTES : DESKTOP_FULL_BYTES;
  const fullSize = mobile ? 900 : 2000;

  const levels = [
    {
      id: 'full',
      scale: 1,
      errorTarget: mobile ? 24 : 16,
      downloadJobs: mobile ? 12 : 24,
      parseJobs: mobile ? 4 : 8,
      dprMax: mobile ? 1.25 : 1.75,
    },
    {
      id: 'half',
      scale: 0.5,
      errorTarget: mobile ? 40 : 32,
      downloadJobs: mobile ? 6 : 12,
      parseJobs: mobile ? 2 : 4,
      dprMax: 1,
    },
    {
      id: 'quarter',
      scale: 0.25,
      errorTarget: mobile ? 56 : 48,
      downloadJobs: mobile ? 3 : 6,
      parseJobs: mobile ? 1 : 2,
      dprMax: 1,
    },
  ];

  return levels.map((level) => {
    const maxBytes = Math.max(24 * MB, Math.round(fullBytes * level.scale));
    const maxSize = Math.max(120, Math.round(fullSize * level.scale));
    return {
      ...level,
      maxBytes,
      minBytes: Math.round(maxBytes * 0.65),
      maxSize,
      minSize: Math.max(40, Math.round(maxSize * 0.7)),
    };
  });
}

/** Cached budgets for the current page load (device class is stable). */
let cachedBudgets = null;

export function getTileSnapshotBudgets() {
  if (!cachedBudgets) cachedBudgets = buildTileSnapshotBudgets();
  return cachedBudgets;
}

export function getTileSnapshotBudget(levelIndex = 0) {
  const budgets = getTileSnapshotBudgets();
  const i = Math.max(0, Math.min(budgets.length - 1, Number(levelIndex) || 0));
  return budgets[i];
}

export function tileSnapshotLevelCount() {
  return getTileSnapshotBudgets().length;
}

/**
 * Apply snapshot budget to a live TilesRenderer (+ optional UnloadTilesPlugin).
 * @param {import('3d-tiles-renderer').TilesRenderer} tiles
 * @param {TileSnapshotBudget} budget
 * @param {{ unloadPlugin?: { bytesTarget?: number } | null }} [opts]
 */
export function applyTileSnapshotBudget(tiles, budget, opts = {}) {
  if (!tiles || !budget) return;
  const cache = tiles.lruCache;
  if (cache) {
    cache.maxBytesSize = budget.maxBytes;
    cache.minBytesSize = budget.minBytes;
    cache.maxSize = budget.maxSize;
    cache.minSize = budget.minSize;
    cache.unloadPercent = 0.2;
  }
  tiles.errorTarget = budget.errorTarget;
  if (tiles.downloadQueue) tiles.downloadQueue.maxJobs = budget.downloadJobs;
  if (tiles.parseQueue) tiles.parseQueue.maxJobs = budget.parseJobs;
  if (opts.unloadPlugin && 'bytesTarget' in opts.unloadPlugin) {
    opts.unloadPlugin.bytesTarget = budget.minBytes;
  }
}

/**
 * @returns {'tiles' | 'osm' | 'off'} next ladder target after a failed Photorealistic attempt
 */
export function nextLadderAfterTilesFail(levelIndex, mapBuildingCount = 0) {
  const next = (Number(levelIndex) || 0) + 1;
  if (next < tileSnapshotLevelCount()) {
    return { kind: 'tiles', levelIndex: next };
  }
  if ((Number(mapBuildingCount) || 0) > 0) {
    return { kind: 'osm', levelIndex: next };
  }
  return { kind: 'off', levelIndex: next };
}

/** Soft user-facing notes when the ladder steps down. */
export function ladderNoteForStep(kind, budgetId) {
  if (kind === 'tiles' && budgetId && budgetId !== 'full') {
    return 'Trying a lighter 3D neighborhood map…';
  }
  if (kind === 'osm') {
    return '3D neighborhood unavailable — switched to map outlines.';
  }
  if (kind === 'off') {
    return 'Map context unavailable — showing lot only.';
  }
  return null;
}

/**
 * Chromium-only heap pressure signal.
 * @returns {boolean}
 */
export function isHeapUnderPressure(ratio = HEAP_PRESSURE_RATIO) {
  try {
    const mem = performance?.memory;
    if (!mem?.jsHeapSizeLimit || !mem.usedJSHeapSize) return false;
    return mem.usedJSHeapSize / mem.jsHeapSizeLimit >= ratio;
  } catch {
    return false;
  }
}
